import { translate } from '@jsverse/transloco';
import { forkJoin, Observable, of, switchMap, tap } from 'rxjs';
import { Utility } from 'src/app/helpers/utility-helper';
import { Payment } from '../models/payment';

import {
  NotificationResponseType,
  SaleResponseStatusType,
} from '../models/transaction-enums.model';
import { InvoiceService } from './invoice/invoice.service';
import { LocationService } from './location/location.service';
import { TransactionService } from './transaction/transaction.service';

export abstract class BaseCardService {
  constructor(
    protected invoiceService: InvoiceService,
    protected locationService: LocationService,
    protected transactionService: TransactionService
  ) {}

  public abstract getInitialPayment(amount: string): Payment;

  public getBalanceDue = () =>
    this.transactionService?.transaction?.balanceDueFixedPointFormat || '0';

  public getFormattedBalanceDue = () =>
    this.transactionService?.transaction?.balanceDueCurrencyFormat || '0';

  public getCurrentCultureDecimalSeparator = () =>
    this.locationService?.location?.currentCultureDecimalSeparator || Utility.DefaultDecimalSeparator;

  public getInvoiceCountryCode = () =>
    this.invoiceService?.invoice?.countryCode || '';

  public excecuteSaleTransaction(payment: Payment): Observable<any> {
    let transactionResponse: any = null;
    const switched = this.transactionService.postTransaction(payment).pipe(
      tap((postTransactionResp: any) => {
        transactionResponse = postTransactionResp;
      }),
      switchMap((postTransactionResp) => {
        const equAmount = payment.RequestedAmount == this.getBalanceDue();
        const approved =
          postTransactionResp.saleResponseStatusTypeId ===
          SaleResponseStatusType.Approval;

        const full = approved && equAmount;

        const partial =
          postTransactionResp.saleResponseStatusTypeId ===
            SaleResponseStatusType.PartialApproval ||
          (approved && !equAmount);

        return partial
          ? this.executePartialPaymentResponse(payment.transactionId)
          : full
          ? this.executeFullPaymentResponse(payment.transactionId)
          : of(null);
      })
    );

    return switched.pipe(
      switchMap((notifications) => {
        return of(transactionResponse);
      })
    );
  }

  private executePartialPaymentResponse(
    transactionId: number
  ): Observable<any> {
    const notificationInfoCustomer = this.invoiceService
      .postNotification(
        this.getNotificationInfoCustomer(NotificationResponseType.partial)
      );

    const notificationInfoMerchant = this.invoiceService
      .postNotification(
        this.getNotificationInfoMerchant(NotificationResponseType.partial)
      );

    const transactionNote = this.transactionService
      .postTransactionNote(
        transactionId,
        translate('TxnNotePartialPaid'),
        translate('TxnNotePaymentMade'),
        100024,
        this.locationService.location.localCurrentDate
      );

    const tasks = {
      type: of(NotificationResponseType.partial),
      notificationInfoCustomer,
      notificationInfoMerchant,
      transactionNote,
    };

    return forkJoin(tasks);
  }

  private executeFullPaymentResponse(transactionId: number): Observable<any> {
    const notificationInfoCustomer = this.invoiceService
      .postNotification(
        this.getNotificationInfoCustomer(NotificationResponseType.full)
    );

    const notificationInfoCustomerReceipt = this.transactionService
      .postNotification(
        this.getNotificationInfoReceipt(transactionId)
      );

    const notificationInfoMerchant = this.invoiceService
      .postNotification(
        this.getNotificationInfoMerchant(NotificationResponseType.full)
      );

    const InvoiceStateTypeId = this.invoiceService
      .patchInvoice(this.invoiceService.login.invoiceId, null, null, 100002);

    const transactionNote = this.transactionService
      .postTransactionNote(
        transactionId,
        translate('TxnNotePaidInFull'),
        translate('Paid'),
        100024,
        this.locationService.location.localCurrentDate
      );

    const tasks = {
      type: of(NotificationResponseType.full),
      notificationInfoCustomer,
      notificationInfoCustomerReceipt,
      notificationInfoMerchant,
      InvoiceStateTypeId,
      transactionNote,
    };
    return forkJoin(tasks);
  }

  private getNotificationInfoCustomer(type: NotificationResponseType): any {
    const invoice = this.invoiceService.invoice;
    //TODO: create notificationInfoCustomer interface
    const notificationInfoCustomer: any = {};

    const notificationChannels = [
      ...(invoice.sendText ? [100000] : []),
      ...(invoice.sendEmail ? [100001] : []),
    ];

    invoice.sendText &&
      (() => (notificationInfoCustomer.Phone = invoice.phoneNumber))();
    invoice.sendEmail &&
      (() => (notificationInfoCustomer.Email = invoice.emailAddress))();

    notificationInfoCustomer.invoiceId = this.invoiceService.login.invoiceId;
    notificationInfoCustomer.InvoiceNotificationTypeId =
      type.valueOf() === NotificationResponseType.full ? 100001 : 100007;
    notificationInfoCustomer.NotificationChannelTypeIds = notificationChannels;
    notificationInfoCustomer.IsMerchantCommunication = false;

    return notificationInfoCustomer;
  }

  private getNotificationInfoMerchant(type: NotificationResponseType): any {
    //TODO: create notificationInfoMerchant interface
    const notificationInfoMerchant: any = {
      invoiceId: this.invoiceService.login.invoiceId,
    };
    notificationInfoMerchant.InvoiceNotificationTypeId =
      type.valueOf() === NotificationResponseType.full ? 100001 : 100007;
    notificationInfoMerchant.NotificationChannelTypeIds = [100001];
    notificationInfoMerchant.IsMerchantCommunication = true;
    return notificationInfoMerchant;
  }

  private getNotificationInfoReceipt(transactionId: number): any {
    //TODO: create notificationInfoReceipt interface    
    const invoice = this.invoiceService.invoice;
    const notificationChannels = [
      ...(invoice.sendText ? [100000] : []),
      ...(invoice.sendEmail ? [100001] : []),
    ];
    const notificationInfoReceipt: any = {
      TransactionId: transactionId,
    };
    notificationInfoReceipt.Email = invoice.emailAddress;
    notificationInfoReceipt.Phone = invoice.phoneNumber;
    notificationInfoReceipt.NotificationChannelTypeIds = notificationChannels;
    notificationInfoReceipt.TransactionNotificationTypeId = 100005;    
    return notificationInfoReceipt;
  }
}
